/**
 * useStudentLiveConnection (web) — l'élève entre dans l'appel de la salle et en sort, dans un
 * navigateur. Extrait de `common/livekit/LKVideoCallPortal.web.js` (lignes 102-303 au 17/10), à
 * comportement identique ; les `.catch(console.log)` passent par la télémétrie.
 *
 *  - au montage, `joinCallV2` (sa réponse dit le son et la main) et le tampon d'événements de la
 *    séance ; au démontage, `leaveCallV2` (sauf après un remplacement ou un crédit), le tampon vidé
 *    vers le serveur, la salle coupée — tenus pour la séance (`callHold`) : la sortie part un tour
 *    après le démontage, et un écran de la même séance remonté entre-temps reprend l'appel sans
 *    écrire `left_call` puis un second `joined_call` ;
 *  - la connexion appartient au SDK et au composant `<LiveKitRoom connect>` de la racine : le web ne
 *    connecte rien lui-même ; son dernier recours (`useLiveKitWebReconnect`) vit dans la salle
 *    (`useLiveRoom`) ;
 *  - la caméra de l'activité : relâchée quand l'appareil photo du navigateur la prend, rendue
 *    ensuite, laissée telle quelle quand la capture réutilise la piste du live
 *    (`decideCameraPortalAction`) ;
 *  - les relevés de la caméra (`cam_toggle`).
 *
 * Le son de la salle (le rendu audio, monté en Dialogue seulement) est posé par l'écran.
 */
import { useConnectionState, useLocalParticipant, useRoomContext } from "@livekit/components-react"
import { ConnectionState, type Room } from "livekit-client"
import { useEffect, useRef } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

import { setMuteVideo } from "../../../data/redux/actions/videoCall"
import LiveEventBuffer from "../../../data/services/LiveEventBuffer"
import { useCameraStateRecorder } from "../room/cameraTelemetry"
import { createCallHold } from "../room/callHold"
import { CameraPortalAction, decideCameraPortalAction } from "../room/cameraReleaseDecision"
import { useInitialCamRecord } from "../room/useInitialCamRecord"
import { type StudentLiveConnection, type StudentLiveConnectionOptions } from "./liveConnectionTypes"
import { joinLiveCall, type JoinedLiveCall } from "./joinLiveCall"
import { enqueueLiveExit } from "./liveExitQueue"

type RootState = {
    liveMode?: { isVideoMuted?: boolean }
    activity?: { cameraOn?: boolean; cameraReusesLiveTrack?: boolean }
}

/** L'appel ouvert d'une séance : la réponse de `joinCall`, partagée par ses écrans. */
type WebCall = { joined: Promise<JoinedLiveCall | null> }

const webCalls = createCallHold<WebCall>()

/** L'entrée dans l'appel, une fois par séance tenue : `joinCall` et le tampon. */
function openWebCall(
    dispatch: unknown,
    liveSessionId: number,
    userId: number | null,
    telemetry: StudentLiveConnectionOptions["telemetry"]
): WebCall {
    const joined = joinLiveCall(dispatch, liveSessionId, telemetry)
    LiveEventBuffer.getInstance().init(liveSessionId, userId)
    return { joined }
}

export function useStudentLiveConnection(options: StudentLiveConnectionOptions): StudentLiveConnection {
    const { liveSessionId, userId, telemetry } = options
    const dispatch = useDispatch()
    const { getState } = useStore()
    const room = useRoomContext() as Room
    const connectionState = useConnectionState()
    const isConnected = connectionState === ConnectionState.Connected
    const isVideoMuted = useSelector((state: RootState) => state.liveMode?.isVideoMuted)
    const activityCameraOn = useSelector((state: RootState) => state.activity?.cameraOn)
    const cameraReusesLiveTrack = useSelector((state: RootState) => state.activity?.cameraReusesLiveTrack)
    const skipLeaveRef = useRef(false)
    const onJoinedRef = useRef(options.onJoined)
    onJoinedRef.current = options.onJoined

    useInitialCamRecord(isConnected, room)

    // Tenus pour la séance (`callHold`) : un écran remonté aussitôt reprend l'appel au lieu de le rejouer.
    useEffect(() => {
        if (liveSessionId == null) return undefined
        const held = webCalls.take(liveSessionId, () => openWebCall(dispatch, liveSessionId, userId, telemetry))
        if (held.resumed) telemetry.transition("released", "held", "call_resumed")
        let mounted = true
        void held.call.joined.then((reply) => {
            if (mounted && reply != null) onJoinedRef.current(reply.data, reply.requestedAt)
        })

        return () => {
            mounted = false
            held.release(() => {
                // La valeur à la sortie, voulue : le remplacement ou le crédit l'a posée entre-temps.
                // eslint-disable-next-line react-hooks/exhaustive-deps
                if (!skipLeaveRef.current) {
                    // Par la file de la sortie : coupée par le réseau, la sortie de l'appel repart à la reprise.
                    void enqueueLiveExit(dispatch as never, getState, { kind: "leave_call", liveSessionId })
                }
                LiveEventBuffer.getInstance().destroy()
                room.disconnect(true).catch((cause: unknown) => {
                    telemetry.error("disconnect", cause)
                })
            })
        }
        // Une fois par séance, comme aujourd'hui.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveSessionId])

    const localParticipant = room?.localParticipant
    useEffect(() => {
        if (!isConnected) return
        const action = decideCameraPortalAction({
            cameraOn: activityCameraOn,
            reusesLiveTrack: cameraReusesLiveTrack,
            isCameraEnabled: localParticipant?.isCameraEnabled,
            isVideoMuted,
        })
        if (action === CameraPortalAction.RELEASE_CAMERA) {
            dispatch(setMuteVideo(false) as never)
            localParticipant?.setCameraEnabled(false).catch((cause: unknown) => {
                telemetry.error("camera_release", cause)
            })
        } else if (action === CameraPortalAction.MUTE_ONLY) {
            dispatch(setMuteVideo(true) as never)
        } else if (action === CameraPortalAction.RESTORE_CAMERA) {
            localParticipant?.setCameraEnabled(true).catch((cause: unknown) => {
                telemetry.error("camera_restore", cause)
            })
        }
        // Comme aujourd'hui : seule la caméra de l'activité réveille cet effet.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityCameraOn, cameraReusesLiveTrack])

    // Le relevé suit le bouton de la caméra, comme celui de la barre d'aujourd'hui (`ControlBarM`).
    const recordCamera = useCameraStateRecorder() as (enabled: boolean) => void
    const { isCameraEnabled } = useLocalParticipant()
    useEffect(() => {
        recordCamera(isCameraEnabled)
    }, [isCameraEnabled, recordCamera])

    // Le web ne connecte rien lui-même (`<LiveKitRoom connect>`) : il n'a pas de relances à borner.
    return { skipLeaveRef, unreachable: false, retry: NOOP }
}

const NOOP = () => {}
